import { isNil, uniqWith } from 'lodash-es';
import { isObservable, of, type Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import {
  QuestionBase,
  type IQuestionBase,
  type SetValueOptions,
} from './question-base';

export interface ISelectItem<T = any, V = any> {
  label?: string;
  value: T;
  disabled?: boolean;
  hidden?: boolean;
  /**
   * @deprecated
   * superceded by the usage of value and trackBy function
   */
  content?: V;
}

export interface IMultiSelectItem<T = any, V = any> extends ISelectItem<T, V> {
  position: number;
}

export type QuestionSelectType<T, V> =
  | IMultiSelectItem<T, V>
  | ISelectItem<T, V>
  | string;

export interface IQuestionMultiBase<
  T = any,
  V = any,
  M extends boolean = boolean,
> extends IQuestionBase<M extends true ? T[] : T> {
  options: QuestionSelectType<T, V>[] | Observable<QuestionSelectType<T, V>[]>;
  multi?: M;
  /**
   * trackBy: function for returning primitive of complex object in toValues (return selected item content if provided or value otherwise)
   */
  trackBy?: (value: T) => any;
  nullLabel?: string;
  addEmptyOptionIfNotRequired?: boolean;
}

export class QuestionMultiBase<
  T = any,
  V = any,
  M extends boolean = boolean,
  TB = any,
>
  extends QuestionBase<M extends true ? T[] : T>
  implements IQuestionMultiBase<T, V, M>
{
  public multi: M;
  public nullLabel: string;
  public trackBy: (value: T) => TB;
  public addEmptyOptionIfNotRequired: boolean;

  private _options: Observable<ISelectItem<T, V>[]> = of([]);

  protected constructor(options: IQuestionMultiBase<T, V, M>) {
    super(options);

    this._formatValue ??= this._defaultFormatValue;

    this.multi = (options.multi === true) as M;
    this.addEmptyOptionIfNotRequired =
      options.addEmptyOptionIfNotRequired ?? true;
    this.initialValue = this._formatValue(this.initialValue);
    this.defaultValue = this._formatValue(this.defaultValue);
    this.trackBy = options.trackBy;
    this.nullLabel = options.nullLabel;

    this.setOptions(options.options);
  }

  public get options(): Observable<ISelectItem<T, V>[]> {
    return this._options;
  }

  public get optionsSync(): ISelectItem<T, V>[] {
    let options: ISelectItem<T, V>[];

    this._options.pipe(take(1)).subscribe(o => {
      options = [...o];
    });

    return options;
  }

  public setOptions(
    values$:
      | (ISelectItem<T, V> | string)[]
      | Observable<(ISelectItem<T, V> | string)[]>,
  ): void {
    if (!isObservable(values$)) {
      values$ = of(values$);
    }

    this._options = values$.pipe(
      map(options =>
        options.map<ISelectItem<T, V>>(option => {
          if ('string' === typeof option) {
            return {
              value: option as any as T,
              label: option,
            };
          }

          return option;
        }),
      ),
      map((options: ISelectItem<T, V>[]) => {
        if (!this.multi) {
          if (
            this.addEmptyOptionIfNotRequired &&
            !this.required &&
            options.length &&
            !options.some(o => o.value === null)
          ) {
            options = [
              {
                value: null,
                label: '',
              },
              ...options,
            ];
          }

          options = uniqWith(
            options,
            (a, b) => a.value === null && a.value === b.value,
          );

          for (const option of options) {
            if (option.value === null) {
              option.label ||= this.nullLabel || '';

              break;
            }

            if (option.disabled === true) {
              break;
            }
          }
        }

        return options;
      }),
    );
  }

  public override equals(
    values: M extends true ? T[] : T,
    ordered: boolean = false,
  ): boolean {
    const currentValues = this.getTrackedValue();

    if (Array.isArray(values)) {
      if (
        !Array.isArray(currentValues) ||
        values.length !== currentValues.length
      ) {
        return false;
      }

      const trackedValues = this.trackBy
        ? values.map(v => this.trackBy(v))
        : values;

      const valuesSorted = ordered ? trackedValues : [...trackedValues].sort();
      const currentValuesSorted = ordered
        ? currentValues
        : [...currentValues].sort();

      for (let i = 0; i < valuesSorted.length; i++) {
        if (valuesSorted[i] !== currentValuesSorted[i]) {
          return false;
        }
      }

      return true;
    }

    return (
      currentValues ===
      (this.trackBy && values ? this.trackBy(values as T) : values)
    );
  }

  public getTrackedValue(): M extends true ? T[] | TB[] : T | TB {
    if (this.trackBy) {
      const v = this.getValue();

      if (Array.isArray(v)) {
        return v.map(z => this.trackBy(z)) as M extends true
          ? T[] | TB[]
          : T | TB;
      }

      return (v ? this.trackBy(v as T) : v) as M extends true
        ? T[] | TB[]
        : T | TB;
    }

    return this.getValue();
  }

  public override getValue(): M extends true ? T[] : T {
    return this._formatValue(super.getValue());
  }

  public override setValue(
    value: M extends true ? T[] : T,
    opts: SetValueOptions = { emitEvent: true, force: false },
  ): void {
    super.setValue(this._formatValue(value), opts);
  }

  private _defaultFormatValue(
    value: M extends true ? T[] : T,
  ): M extends true ? T[] : T {
    if (isNil(value)) {
      return value;
    }

    if (this.multi) {
      return Array.isArray(value)
        ? value
        : ([value] as M extends true ? T[] : T);
    }

    return Array.isArray(value) ? value[0] : value;
  }
}
